// src/features/receipts/InsuranceReceipts.jsx
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  Select,
  Card,
  Modal,
  Flex,
  Grid,
} from "antd";
import InputNumberField from "../../components/Input/InputNumber"; // your existing component
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchInsuranceReceiptCode,
  fetchInsuranceDueVouchers,
  fetchInsuranceDueEmis,
  fetchInsuranceCustomerBalance,
  saveInsuranceReceipt,
  fetchInsuranceReceipts,
  fetchInsuranceDirectExpenseAccounts,
  deleteInsuranceReceipt,
} from "./insuranceReceiptThunk";
import {
  fetchLoadIntoAccsExcludeCheque,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchCustomers,
} from "../../store/commonThunks/lookups"; // reuse existing lookups thunks
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import { dateTimeFormat, getAccessType } from "../../lib/functions";
import {
  resetInsuranceReceipts,
  setInsuranceEntryDate,
  setIsEditInsuranceReceipt,
  setSelectedInsuranceReceipt,
} from "./insuranceReceiptSlice";

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const InsuranceReceipts = () => {
  const { useBreakpoint } = Grid;
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const [form] = Form.useForm();

  // local state
  const [rows, setRows] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagGroupList, setTagGroupList] = useState([]);
  let [access_type, access_type_set] = useState("");

  const {
    entryDate,
    receiptCode,
    dueVouchers,
    dueEmis,
    balances,
    selectedReceipt,
    isEditReceipt,
    directExpense,
    receipts,
    loading,
  } = useSelector((s) => s.insuranceReceipts);
  const { loadIntoAccounts, customers } = useSelector((s) => s.lookups);


  // init
  useEffect(() => {
    access_type_set(getAccessType("debtor_receipt"));
    dispatch(fetchInsuranceReceiptCode());
    dispatch(fetchCustomers());
    dispatch(fetchInsuranceDirectExpenseAccounts());
    dispatch(fetchLoadIntoAccsExcludeCheque());
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) => {
        return Promise.all(
          groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap())
        );
      })
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      });

    setRows([
      {
        key: Date.now() + Math.random(),
        into_acc_name: "",
        from_insurance: null,
        voucher_no: "",
        emi_no: "",
        amount: 0,
        discount: 0,
      },
    ]);
  }, [dispatch]);

  // load receipts when editing directly by id
  useEffect(() => {
    if (id && !selectedReceipt) {
      dispatch(fetchInsuranceReceipts())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.rcv_id == id);
          if (found) {
            dispatch(setSelectedInsuranceReceipt(found));
            dispatch(setIsEditInsuranceReceipt(true));
          }
        });
    }
  }, [id, selectedReceipt, dispatch]);

  // populate edit form
  useEffect(() => {
    if (isEditReceipt && selectedReceipt) {
      form.setFieldsValue({
        rcv_date: dayjs(selectedReceipt.created_date),
        description: selectedReceipt.narration,
        tags: selectedReceipt.tag_field_id || [],
      });

      setRows(
        (selectedReceipt.rcvDetail || []).map((d) => ({
          key: Date.now() + Math.random(),
          into_acc_id: d.into_acc_id,
          into_acc_name: d.into_acc_name,
          into_acc_type_id: d.into_acc_type_id,
          from_insurance: { acc_id: d.from_acc_id, acc_name: d.from_acc_name },
          voucher_no: d.voucher_no,
          emi_id: d.emi_id,
          emi_no: d.emi_no,
          amount: d.rcv_amount,
          discount: d.discount_amount,
          pdc_date: d.pdc_date || null,
        }))
      );
    }
  }, [isEditReceipt, selectedReceipt, form]);

  // when insurance selected
  const handleInsuranceSelect = (insurance) => {
    setSelectedInsurance(insurance);
    if (insurance?.acc_id) {
      // fetch due vouchers & emis & balance for this insurance-customer pair
      dispatch(
        fetchInsuranceDueVouchers({
          insuranceId: insurance.acc_code,
          customerId: insurance.acc_id,
        })
      );
      dispatch(fetchInsuranceDueEmis(insurance.acc_id));
      dispatch(fetchInsuranceCustomerBalance(insurance.acc_id));
    }
  };

  // add/remove rows
  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key: Date.now() + Math.random(),
        into_acc_name: "",
        from_insurance: selectedInsurance,
        voucher_no: "",
        emi_no: "",
        amount: 0,
        discount: 0,
      },
    ]);
  };
  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedInsuranceReceipt(null));
    dispatch(setIsEditInsuranceReceipt(false));
    navigate("/accounts/debitor-insurance-receipts");
  };

  const onFinish = async (values) => {
    try {
      const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
      const totalDiscount = rows.reduce(
        (s, r) => s + Number(r.discount || 0),
        0
      );

      const insurance = rows[0]?.from_insurance;
      if (!insurance?.acc_id) {
        Modal.error({
          title: "Please select an Insurance company before saving.",
        });
        return;
      }

      const allSameInsurance = rows.every(
        (r) => r.from_insurance?.acc_id === insurance?.acc_id
      );
      if (!allSameInsurance) {
        Modal.error({
          title: "All rows must belong to the same Insurance company",
        });
        return;
      }

      // row-level validation
      for (let r of rows) {
        if (!r.voucher_no) {
          Modal.error({ title: "Please select Voucher before saving." });
          return;
        }
        if (r.amount == null || r.amount <= 0) {
          Modal.error({ title: "Please enter a valid Amount before saving." });
          return;
        }
        if (!r.into_acc_name) {
          Modal.error({ title: "Please select Account before saving." });
          return;
        }
      }

      // previous due (optional)
      const prevDue = balances[insurance.acc_id]?.total_balance || 0;

      const rcvPayload = {
        rcv_id: isEditReceipt ? selectedReceipt.rcv_id : 0,
        action: isEditReceipt ? "update" : "create",
        rcv_code: receiptCode,
        created_date: values.rcv_date
          ? values.rcv_date.toISOString()
          : dayjs().toISOString(),
        narration: values.description || "",
        rcv_total: totalAmount,
        discount_total: totalDiscount,
        tax_total: 0,
      };
      if (selectedTags.length > 0) rcvPayload.tag_field_id = selectedTags;

      const payload = {
        contact_no: insurance?.contact_no || "",
        is_smg: "yes",
        msg: `Dear ${insurance.acc_name}, Your payment amount is ${Number(
          totalAmount
        ).toFixed(2)}, Your Previous Due is ${Number(prevDue).toFixed(
          2
        )} & Current Due is ${(prevDue - totalAmount).toFixed(2)}`,
        rcv: rcvPayload,
        rcvDetail: rows.map((r) => ({
          into_acc_id: r.into_acc_id || 0,
          into_acc_name: r.into_acc_name || "",
          into_acc_type_id: r.into_acc_type_id || "",
          from_acc_id: r.from_insurance?.acc_id || 0,
          from_acc_name: r.from_insurance?.acc_name || "",
          voucher_no: r.voucher_no || "",
          emi_id: r.emi_id || 0,
          emi_no: r.emi_no || "",
          rcv_amount: Number(r.amount) || 0,
          discount_amount: Number(r.discount) || 0,
          tax_amount: 0,
          direct_income_id: directExpense?.acc_id,
          current_liability_id: 0,
          rcv_total: (Number(r.amount) || 0) - (Number(r.discount) || 0),
          amount: Number(r.amount) || 0,
          pdc_date: r.pdc_date
            ? dayjs(r.pdc_date).toISOString()
            : dayjs().toISOString(),
        })),
      };

      const res = await dispatch(saveInsuranceReceipt(payload)).unwrap();
      Modal.success({ title: res.msg || "Receipt saved successfully" });

      // reset
      dispatch(setSelectedInsuranceReceipt(null));
      dispatch(setIsEditInsuranceReceipt(false));
      form.resetFields();
      setRows([]);
      navigate("/accounts/debitor-pharma-receipts");
    } catch (err) {
      Modal.error({ title: err.message || "Failed to save receipt" });
    }
  };

  // table columns
  const emiColumn = {
    title: "EMI No",
    dataIndex: "emi_no",
    render: (_, record, index) => {
      return (
        <div>
          <SearchListAutoComplete
            entityType="vouchers"
            style={{ width: "100%" }}
            optionsList={(dueEmis[selectedInsurance?.acc_id] || []).map(
              (e) => ({
                sale_id: e.emi_id,
                display_text: e.emi_no,
                raw: e,
              })
            )}
            onSelect={(obj) => {
              const newRows = [...rows];
              newRows[index].emi_id = obj?.sale_id || null;
              newRows[index].emi_no = obj?.display_text || "";
              newRows[index].emi_details = obj?.raw || null;
              setRows(newRows);
            }}
            placeholder="Select EMI"
            showAddNew={false}
          />
          {record.emi_details && (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <span style={{ color: "red", marginRight: 8 }}>
                Emi Due {record.emi_details.due}
              </span>
            </div>
          )}
        </div>
      );
    },
  };

  const pdcColumn = rows.some((r) => r.into_acc_name?.toLowerCase() === "pdc")
    ? [
        {
          title: "PDC Date",
          dataIndex: "pdc_date",
          width: 160,
          render: (_, record, index) =>
            record.into_acc_name?.toLowerCase() === "pdc" ? (
              <DatePicker
                style={{ width: "100%" }}
                value={record.pdc_date ? dayjs(record.pdc_date) : null}
                onChange={(d) => {
                  const newRows = [...rows];
                  newRows[index].pdc_date = d ? d.toISOString() : null;
                  setRows(newRows);
                }}
                format={dateTimeFormat}
              />
            ) : (
              "-"
            ),
        },
      ]
    : [];

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 50 },
    {
      title: "From Insurance",
      dataIndex: "from_insurance",
      render: (insurance) => (
        <SearchListAutoComplete
          entityType="insurance"
          style={{ width: "100%" }}
          optionsList={(customers || [])
            .filter((cus) => cus.acc_type_name === "Insurance")
            .map((c) => ({
              sale_id: c.acc_id,
              display_text: c.acc_name,
              raw: c,
            }))}
          onSelect={(obj) => {
            const newInsurance = obj?.raw;
            // ensure all rows same insurance
            const existingInsurance = rows.find(
              (r) => r.from_insurance?.acc_id
            )?.from_insurance;
            if (
              existingInsurance &&
              existingInsurance.acc_id !== newInsurance.acc_id
            ) {
              Modal.error({
                title: "All rows must belong to the same Insurance company",
              });
              return;
            }
            handleInsuranceSelect(newInsurance);
            const newRows = [...rows];
            newRows[0].from_insurance = obj?.raw;
            setRows(newRows);
          }}
          placeholder="Insurance Company"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Total Due",
      dataIndex: "total_due",
      render: () =>
        selectedInsurance?.acc_id
          ? balances[selectedInsurance?.acc_id]?.total_balance || 0
          : 0,
    },
    {
      title: "Voucher",
      dataIndex: "voucher_no",
      render: (_, record, index) => {
        const insuranceVouchers = selectedInsurance?.acc_id
          ? dueVouchers[selectedInsurance.acc_id] || []
          : [];
        return (
          <div>
            <SearchListAutoComplete
              data-testid="voucher-select"
              entityType="vouchers"
              style={{ width: "100%" }}
              optionsList={insuranceVouchers.map((v) => ({
                sale_id: v.voucher_no,
                display_text: v.display_text,
                raw: v,
              }))}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].voucher_no = obj?.raw?.voucher_no || "";
                newRows[index].voucher_details = obj?.raw || null;
                setRows(newRows);
              }}
              placeholder="Select Voucher"
              showAddNew={false}
            />

            {record.voucher_details && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <span style={{ color: "red", marginRight: 8 }}>
                  Due{" "}
                  {record.voucher_details.insurance_amount -
                    record.voucher_details.partialPaid}
                </span>
                <span style={{ color: "brown", marginRight: 8 }}>
                  Discount {record.voucher_details.discountAmount}
                </span>
                <span style={{ color: "green", marginRight: 8 }}>
                  Paid {record.voucher_details.partialPaid}
                </span>
                <span>Bill {record.voucher_details.total_amount}</span>
              </div>
            )}
          </div>
        );
      },
    },
    ...(dueEmis && Object.keys(dueEmis).length > 0 ? [emiColumn] : []),
    {
      title: "Received Into",
      dataIndex: "into_acc_name",
      render: (_, record, index) => (
        <SearchListAutoComplete
          entityType="accounts"
          style={{ width: "100%" }}
          optionsList={(loadIntoAccounts || []).map((acc) => ({
            sale_id: acc.acc_id,
            display_text: acc.display_text || acc.acc_name,
            raw: acc,
          }))}
          onSelect={(obj) => {
            const newRows = [...rows];
            newRows[index].into_acc_name = obj?.raw?.acc_name || "";
            newRows[index].into_acc_id = obj?.raw?.acc_id || 0;
            newRows[index].into_acc_type_id = obj?.raw?.acc_type_id || "";
            setRows(newRows);
          }}
          placeholder="Cash / Bank / Cheque"
          showAddNew={false}
        />
      ),
    },
    ...pdcColumn,
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, record, index) => (
        <InputNumberField
          data-testid="amount-input"
          min={0}
          value={record.amount}
          stringMode
          step={1}
          precision={2}
          style={{ width: "100%" }}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].amount = val || 0;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      render: (_, record, index) => (
        <InputNumberField
          min={0}
          value={record.discount}
          step={1}
          precision={2}
          stringMode
          style={{ width: "100%" }}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].discount = val || 0;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveRow(record.key)}>
          Delete
        </Button>
      ),
    },
  ];

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const totalDiscount = rows.reduce(
    (sum, r) => sum + Number(r.discount || 0),
    0
  );

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Insurance Receipts</Title>

      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        data-testid="insurance-form"
        initialValues={{ rcv_date: dayjs(entryDate) }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
          data-testid="form-header-section"
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Receipt Code">
              <Input value={receiptCode} disabled />
            </Form.Item>

            <Form.Item
              name="rcv_date"
              label="Receipt Date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setInsuranceEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
              />
            </Form.Item>

            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                onChange={(ids) => setSelectedTags(ids)}
                options={tagGroupList.map((item) => ({
                  value: item.id,
                  label: item.combinedField,
                }))}
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
        >
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey={(record, index) => record.key}
          bordered
          scroll={{ x: "max-content" }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <strong>
                Total Amount: {totalAmount} | Total Discount: {totalDiscount}
              </strong>
            </div>
          )}
        />

        <Form.Item name="description" label="Description">
          <TextArea rows={3} data-testid="description-input" />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              dispatch(resetInsuranceReceipts());
              setSelectedInsurance(null);
              setSelectedVoucher(null);
              setSelectedTags([]);
              setRows([]);
              dispatch(fetchInsuranceReceiptCode());
            }}
          >
            Reset
          </Button>

          {access_type === "WRITE" && (
            <Button
              type="primary"
              htmlType="submit"
              data-testid="insurance-btn-save"
            >
              Save Receipt
            </Button>
          )}
        </Space>
      </Form>
    </div>
  );
};

export default InsuranceReceipts;
